// Route: /tutor — список сессий (sessions list).
// URL state: ?q, ?mode, ?demo
// MSW endpoint: GET /api/tutor/sessions.
//
// См. docs/ui-spec/tutor.sessions.{desktop,mobile}.md.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button, ErrorState, QuotaIndicator, SectionHeader, Skeleton } from "@studyhub/ui";
import { SegmentedControl } from "@studyhub/ui/components/segmented-control";
import {
	IconBolt,
	IconLibrary,
	IconMessage2,
	IconMessages,
	IconPlus,
	IconSparkles,
	IconWorld,
} from "@tabler/icons-react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { SECTION_TINT } from "@/components/navigation/section-icons";
import {
	LIST_PAGE_GRID,
	LIST_PAGE_SHELL,
	LIST_PAGE_STACK,
	ListToolbar,
} from "@/components/shared/entity-list";
import {
	SectionIntro,
	SectionIntroReopen,
	useSectionIntro,
} from "@/components/shared/section-intro";
import {
	type ChatSessionListItem,
	type TutorMode,
	useTutorSessions,
} from "@/components/tutor/_api";
import { SessionsEmptyState } from "@/components/tutor/sessions-list/empty-state";
import { SessionCard } from "@/components/tutor/sessions-list/session-card";
import { useQuota } from "@/hooks/use-quota";
import type { ViewMode } from "@/hooks/use-view-mode";
import { useViewMode } from "@/hooks/use-view-mode";
import { useFeatureFlag } from "@/lib/observability/feature-flags";

const searchSchema = z.object({
	q: z.string().optional(),
	mode: z.enum(["regular", "materials", "web-search"]).optional(),
	demo: z.enum(["default", "loading", "empty", "error", "quota-exceeded"]).optional(),
});

export const Route = createFileRoute("/tutor/")({
	validateSearch: tolerantSearch(searchSchema),
	component: TutorSessionsPage,
});

type DateGroup = "today" | "yesterday" | "week" | "older";

function groupByDate(items: ChatSessionListItem[]): Record<DateGroup, ChatSessionListItem[]> {
	const now = Date.now();
	const dayMs = 24 * 60 * 60 * 1000;
	const groups: Record<DateGroup, ChatSessionListItem[]> = {
		today: [],
		yesterday: [],
		week: [],
		older: [],
	};
	for (const item of items) {
		const diff = now - new Date(item.updatedAt).getTime();
		if (diff < dayMs) groups.today.push(item);
		else if (diff < 2 * dayMs) groups.yesterday.push(item);
		else if (diff < 7 * dayMs) groups.week.push(item);
		else groups.older.push(item);
	}
	return groups;
}

function TutorSessionsPage(): React.ReactElement {
	const { t } = useTranslation("tutor");
	const { t: tc } = useTranslation("common");
	const intro = useSectionIntro("tutor");
	const search = Route.useSearch();
	const navigate = useNavigate({ from: "/tutor/" });
	const quota = useQuota("tokens");
	const unifiedChat = useFeatureFlag("tutor-unified-chat-enabled");

	const sessionsQuery = useTutorSessions();
	// Чаты по умолчанию — список (плотнее и удобнее сканировать длинную ленту).
	const [view, setView] = useViewMode("tutor", "list");

	const allSessions = useMemo(() => sessionsQuery.data?.items ?? [], [sessionsQuery.data]);

	const sessions = useMemo(() => {
		let items: ChatSessionListItem[] = allSessions;
		if (search.demo === "empty") items = [];
		// Unified chat больше не фильтрует по режимам (режимы скрыты).
		if (!unifiedChat && search.mode) items = items.filter((s) => s.mode === search.mode);
		if (search.q) {
			const q = search.q.toLowerCase();
			items = items.filter(
				(s) => s.title.toLowerCase().includes(q) || (s.preview?.toLowerCase().includes(q) ?? false),
			);
		}
		return items;
	}, [allSessions, search.demo, search.mode, search.q, unifiedChat]);

	const grouped = useMemo(() => groupByDate(sessions), [sessions]);

	function updateSearch(next: { q?: string; mode?: TutorMode }) {
		void navigate({
			search: (prev) => ({ ...prev, q: next.q || undefined, mode: next.mode }),
			replace: true,
		});
	}

	const isLoading = sessionsQuery.isLoading || search.demo === "loading";
	const isError = sessionsQuery.isError || search.demo === "error";

	const hasLimit = quota.limit !== null;
	const subtitle = t(hasLimit ? "sessions.subtitle" : "sessions.subtitleNoLimit", {
		defaultValue: hasLimit ? "{count} chats · {used}/{limit}k tokens" : "{count} chats",
		count: allSessions.length,
		used: Math.round(quota.used / 1000),
		limit: hasLimit ? Math.round((quota.limit ?? 0) / 1000) : 0,
	});

	return (
		<div className={LIST_PAGE_SHELL}>
			{/* Заголовок секции «Картотека» */}
			<SectionHeader
				icon={<IconMessage2 />}
				category="ai"
				tint={SECTION_TINT.tutor}
				title={t("title")}
				description={subtitle}
				actions={
					<>
						<SectionIntroReopen intro={intro} />
						<Button variant="ghost" asChild className="hidden cursor-pointer sm:inline-flex">
							<Link to="/tutor/scenarios">
								<IconSparkles size={16} />
								{t("sessions.scenarios", { defaultValue: "Сценарии" })}
							</Link>
						</Button>
						<Button variant="ghost" asChild className="hidden cursor-pointer sm:inline-flex">
							<Link to="/tutor/skills">
								<IconBolt size={16} />
								{t("sessions.skills", { defaultValue: "Навыки" })}
							</Link>
						</Button>
						<Button asChild className="cursor-pointer">
							<Link to="/tutor/$sessionId" params={{ sessionId: "new" }}>
								<IconPlus size={16} />
								<span className="hidden sm:inline">{t("newChat")}</span>
							</Link>
						</Button>
					</>
				}
			/>

			{/* Баннер-объяснение раздела: текст из манифеста модуля, состояние
			    «свёрнут» — серверное (см. section-intro.tsx). */}
			<SectionIntro intro={intro} />

			{/* Единый тулбар: поиск + сегмент режимов + квота + переключатель вида */}
			<ListToolbar
				search={{
					value: search.q ?? "",
					onChange: (q) => updateSearch({ q, mode: unifiedChat ? undefined : search.mode }),
					placeholder: t("sessions.searchPlaceholder", { defaultValue: "Поиск по чатам..." }),
				}}
				segments={
					unifiedChat ? undefined : (
						<ModeFilter
							active={search.mode}
							onChange={(mode) => updateSearch({ q: search.q, mode })}
						/>
					)
				}
				filters={
					<QuotaIndicator
						used={quota.used}
						limit={quota.limit}
						label={tc("quota.remaining")}
						className="hidden shrink-0 sm:inline-flex"
					/>
				}
				view={view}
				onViewChange={setView}
			/>

			{/* Состояния */}
			{isError ? (
				<ErrorState
					title={t("sessions.error.title", {
						defaultValue: "Не удалось загрузить чаты",
					})}
					action={
						<Button
							variant="outline"
							size="sm"
							onClick={() => void sessionsQuery.refetch()}
							className="cursor-pointer"
						>
							{t("sessions.error.retry", { defaultValue: "Повторить" })}
						</Button>
					}
				/>
			) : isLoading ? (
				<SessionsLoadingSkeleton />
			) : sessions.length === 0 ? (
				<SessionsEmptyState hasFilter={!!(search.q || search.mode)} />
			) : (
				<div className="flex flex-col gap-6">
					{(Object.keys(grouped) as DateGroup[]).map((key) =>
						grouped[key].length > 0 ? (
							<DateGroupSection
								key={key}
								view={view}
								title={t(`sessions.dateGroups.${key}`)}
								count={grouped[key].length}
								items={grouped[key]}
							/>
						) : null,
					)}
				</div>
			)}
		</div>
	);
}

function DateGroupSection({
	title,
	count,
	items,
	view,
}: {
	title: string;
	count: number;
	items: ChatSessionListItem[];
	view: ViewMode;
}) {
	return (
		<section>
			<h2 className="sticky top-[var(--topbar-height)] z-10 -mx-1 mb-2 flex items-center gap-2 bg-background/90 px-1 py-1.5 backdrop-blur-sm">
				<span className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
					{title}
				</span>
				<span className="rounded-[var(--radius-xs)] bg-muted px-1.5 py-0.5 text-2xs font-medium tabular-nums text-muted-foreground">
					{count}
				</span>
			</h2>
			<div className={view === "grid" ? LIST_PAGE_GRID : LIST_PAGE_STACK}>
				{items.map((s) => (
					<SessionCard key={s.id} session={s} view={view} />
				))}
			</div>
		</section>
	);
}

function ModeFilter({
	active,
	onChange,
}: {
	active: TutorMode | undefined;
	onChange: (mode: TutorMode | undefined) => void;
}) {
	const { t } = useTranslation("tutor");
	return (
		<SegmentedControl
			scrollable
			aria-label={t("sessions.filters.all", { defaultValue: "Режим" })}
			value={active ?? "all"}
			onValueChange={(v) => onChange(v === "all" ? undefined : (v as TutorMode))}
			options={[
				{
					value: "all",
					label: t("sessions.filters.all", { defaultValue: "Все" }),
					icon: <IconMessages size={16} />,
				},
				{ value: "regular", label: t("modes.regular"), icon: <IconMessage2 size={16} /> },
				{ value: "materials", label: t("modes.materials"), icon: <IconLibrary size={16} /> },
				{ value: "web-search", label: t("modes.webSearch"), icon: <IconWorld size={16} /> },
			]}
		/>
	);
}

function SessionsLoadingSkeleton() {
	return (
		<div className="flex flex-col gap-6">
			{[0, 1].map((g) => (
				<section key={g}>
					<Skeleton className="mb-2 h-4 w-24" />
					<div className={LIST_PAGE_STACK}>
						{[0, 1, 2].map((i) => (
							<div
								key={i}
								className="flex items-start gap-3 rounded-[var(--radius-card)] border border-border bg-card px-3.5 py-3 shadow-e1"
							>
								<Skeleton className="size-9 shrink-0 rounded-[var(--radius-tile)]" />
								<div className="flex-1">
									<Skeleton className="mb-2 h-4 w-3/5" />
									<Skeleton className="mb-2 h-3 w-4/5" />
									<Skeleton className="h-3 w-2/5" />
								</div>
							</div>
						))}
					</div>
				</section>
			))}
		</div>
	);
}
